import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { CardPaymentMethodType } from "@/api-v3";
import { isCardStatusActive } from "@/features/card/utils/card-status";
import {
  type NewCardSuccessState,
  useCompletePaymentMethodChange,
} from "@/features/payment-methods";
import { usePaymentMethods } from "@/features/payments";
import {
  canActivate,
  type PaymentAccountCard,
} from "@/features/payments/types/payment-account-cards";
import { noop } from "@/lib/utils";
import { AppRoute } from "@/types/routes";

import { CardViewer } from "../components/card-viewer";
import {
  OnboardingComponent,
  type OnboardingDataItem,
} from "../components/onboarding-component";

/** The Card Type the creation flow passed, or undefined when absent or unknown. */
const getCreatedCardType = (
  state: unknown
): CardPaymentMethodType | undefined => {
  const cardType = (state as Partial<NewCardSuccessState> | null)?.cardType;
  return cardType && Object.values(CardPaymentMethodType).includes(cardType)
    ? cardType
    : undefined;
};

// The card of the Card Type just created, or the first virtual card when the
// page is reached without one. Stripe only issues a card once the old one of
// the same type is locked, so the locked one may still be listed: prefer active.
const findNewCard = (
  cards: PaymentAccountCard[],
  cardType: CardPaymentMethodType | undefined
): PaymentAccountCard | undefined => {
  const candidates = cards.filter((card) =>
    cardType ? card.card_type === cardType : !canActivate(card.card_type)
  );
  return (
    candidates.find((card) => isCardStatusActive(card.status)) ?? candidates[0]
  );
};

export const NewCardSuccessPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { cards } = usePaymentMethods();
  const { completeOrNavigate } = useCompletePaymentMethodChange();
  const newCard = findNewCard(cards, getCreatedCardType(location.state));

  function getData(): OnboardingDataItem[] {
    return [
      {
        title: t("vcc_created_title"),
        imagePosition: "belowHeader",
        imageSrc: () => (
          <div className="max-w-md mx-auto">
            {newCard && (
              <CardViewer
                key="v2"
                card={newCard}
                isVisible={false}
                onRegisterHandler={noop}
              />
            )}
          </div>
        ),
      },
    ];
  }

  // Created on a Claim Detour: the card is theirs, so finish the change and
  // hand them back to the claim they were starting. Otherwise, home.
  const onFinish = () => completeOrNavigate(AppRoute.HOME);

  return (
    <OnboardingComponent
      data={getData()}
      finishLabel={t("ok")}
      onFinish={onFinish}
    />
  );
};
